// Pure helpers for the document structure the user assembles. The structure is
// a tree of sections, each holding an ordered list of content blocks and its
// own sub-sections.

export const MAX_DEPTH = 3;

export const newId = (prefix) =>
  `${prefix}-${typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;

export const createSection = (title = "") => ({
  id: newId("section"),
  title,
  blocks: [],
  children: []
});

export const createBlock = (source) => ({
  id: newId("block"),
  documentId: source.documentId,
  nodeId: source.nodeId,
  label: source.label,
  filename: source.filename,
  type: source.type,
  level: source.level,
  blockCount: source.blockCount
});

/** Colour and short label for a source node or a block copied from one. */
export const sourceTypeTag = (item) => {
  if (item.type === "heading") return { color: "blue", label: `H${item.level}` };
  if (item.type === "table") return { color: "green", label: "Tbl" };
  if (item.type === "body") return { color: "gold", label: "Text" };
  if (item.type === "contentControl") return { color: "magenta", label: "CC" };
  return { color: "default", label: "Doc" };
};

const mapTree = (sections, transform) =>
  sections.map((section) => {
    const mapped = transform(section);
    return { ...mapped, children: mapTree(mapped.children, transform) };
  });

export const updateSection = (sections, sectionId, updater) =>
  mapTree(sections, (section) => (section.id === sectionId ? updater(section) : section));

export const removeSection = (sections, sectionId) =>
  sections
    .filter((section) => section.id !== sectionId)
    .map((section) => ({ ...section, children: removeSection(section.children, sectionId) }));

export const addSection = (sections, parentId, section) => {
  if (!parentId) return [...sections, section];
  return updateSection(sections, parentId, (parent) => ({
    ...parent,
    children: [...parent.children, section]
  }));
};

export const findSection = (sections, sectionId) => {
  for (const section of sections) {
    if (section.id === sectionId) return section;
    const found = findSection(section.children, sectionId);
    if (found) return found;
  }
  return null;
};

export const depthOf = (sections, sectionId, depth = 1) => {
  for (const section of sections) {
    if (section.id === sectionId) return depth;
    const found = depthOf(section.children, sectionId, depth + 1);
    if (found) return found;
  }
  return 0;
};

/** Move a section up or down among its own siblings. */
export const moveSection = (sections, sectionId, offset) => {
  const index = sections.findIndex((section) => section.id === sectionId);

  if (index === -1) {
    return sections.map((section) => ({
      ...section,
      children: moveSection(section.children, sectionId, offset)
    }));
  }

  const target = index + offset;
  if (target < 0 || target >= sections.length) return sections;

  const reordered = [...sections];
  [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
  return reordered;
};

export const addBlock = (sections, sectionId, block, index = null) =>
  updateSection(sections, sectionId, (section) => {
    const blocks = [...section.blocks];
    blocks.splice(index === null || index > blocks.length ? blocks.length : index, 0, block);
    return { ...section, blocks };
  });

export const removeBlock = (sections, blockId) =>
  mapTree(sections, (section) =>
    section.blocks.some((block) => block.id === blockId)
      ? { ...section, blocks: section.blocks.filter((block) => block.id !== blockId) }
      : section
  );

export const moveBlock = (sections, blockId, offset) =>
  mapTree(sections, (section) => {
    const index = section.blocks.findIndex((block) => block.id === blockId);
    if (index === -1) return section;

    const target = index + offset;
    if (target < 0 || target >= section.blocks.length) return section;

    const blocks = [...section.blocks];
    [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
    return { ...section, blocks };
  });

export const sectionHoldingBlock = (sections, blockId) => {
  for (const section of sections) {
    if (section.blocks.some((block) => block.id === blockId)) return section;
    const found = sectionHoldingBlock(section.children, blockId);
    if (found) return found;
  }
  return null;
};

/** Move a block to another section, or to a new position within its own. */
export const relocateBlock = (sections, blockId, targetSectionId, index = null) => {
  const origin = sectionHoldingBlock(sections, blockId);
  if (!origin) return sections;

  const block = origin.blocks.find((candidate) => candidate.id === blockId);
  if (!block) return sections;

  if (origin.id === targetSectionId) {
    const from = origin.blocks.findIndex((candidate) => candidate.id === blockId);
    const to = index === null ? origin.blocks.length - 1 : Math.min(index, origin.blocks.length - 1);
    if (from === to) return sections;

    return updateSection(sections, targetSectionId, (section) => {
      const blocks = [...section.blocks];
      blocks.splice(from, 1);
      blocks.splice(to, 0, block);
      return { ...section, blocks };
    });
  }

  return addBlock(removeBlock(sections, blockId), targetSectionId, block, index);
};

export const countSections = (sections) =>
  sections.reduce((total, section) => total + 1 + countSections(section.children), 0);

export const countBlocks = (sections) =>
  sections.reduce(
    (total, section) => total + section.blocks.length + countBlocks(section.children),
    0
  );

const sectionPayload = (section) => ({
  title: section.title,
  blocks: section.blocks.map((block) => ({
    documentId: block.documentId,
    nodeId: block.nodeId,
    label: block.label
  })),
  children: section.children.map(sectionPayload)
});

/** Drop the ids the UI needs but the server does not. */
export const toPayload = (sections) => ({ sections: sections.map(sectionPayload) });

/** Sections referring to documents that are no longer loaded lose those blocks. */
export const pruneMissingDocuments = (sections, documentIds) =>
  mapTree(sections, (section) => {
    const blocks = section.blocks.filter((block) => documentIds.includes(block.documentId));
    return blocks.length === section.blocks.length ? section : { ...section, blocks };
  });
